import { useEffect, useState, type FormEvent } from 'react';
import {
  api,
  TEMPLATES,
  templateLabel,
  type AskResult,
  type Highlight,
  type MeetingDetail,
} from '../lib/api.js';
import { formatMsClock } from '../lib/format.js';
import { currentBrand } from '../lib/brand.js';

export function AskPanel({ meetingId, enabled }: { meetingId: string; enabled: boolean }): JSX.Element {
  const [asks, setAsks] = useState<AskResult[]>([]);
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    void api.listAsks(meetingId).then(setAsks).catch(() => { /* ignore */ });
  }, [meetingId, enabled]);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    const q = question.trim();
    if (!q || busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.ask(meetingId, q);
      setAsks((prev) => [...prev, result]);
      setQuestion('');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="panel" style={{ marginTop: 12 }}>
      <h3 className="panel__title">Ask {currentBrand().name}</h3>
      {!enabled && <p className="panel__subtle">Available once a transcript exists.</p>}
      {enabled && (
        <>
          <div className="ask-thread">
            {asks.map((a) => (
              <div key={a.id} className="ask-item">
                <div className="ask-item__q">Q: {a.question}</div>
                <div className="ask-item__a">{a.answer}</div>
                <div className="panel__subtle" style={{ marginTop: 4 }}>via {a.source}</div>
              </div>
            ))}
          </div>
          <form onSubmit={(e) => void onSubmit(e)} style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <input
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Ask about this meeting…"
              disabled={busy}
            />
            <button type="submit" className="btn" disabled={busy || !question.trim()}>
              {busy ? '…' : 'Ask'}
            </button>
          </form>
          {error && <div className="form-error" style={{ marginTop: 8 }}>{error}</div>}
        </>
      )}
    </div>
  );
}

export function HighlightsPanel({
  meetingId,
  seek,
  transcriptLength,
}: {
  meetingId: string;
  seek: (ms: number) => void;
  transcriptLength: number;
}): JSX.Element {
  const [highlights, setHighlights] = useState<Highlight[]>([]);
  const [startMs, setStartMs] = useState('');
  const [endMs, setEndMs] = useState('');
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function load(): Promise<void> {
    try {
      setHighlights(await api.listHighlights(meetingId));
    } catch {
      /* ignore */
    }
  }

  useEffect(() => {
    void load();
  }, [meetingId]);

  async function onSave(e: FormEvent): Promise<void> {
    e.preventDefault();
    const s = Number(startMs);
    const en = Number(endMs);
    if (!Number.isFinite(s) || !Number.isFinite(en) || en <= s) {
      setError('Enter start/end ms (end > start). Tip: click a transcript line for start.');
      return;
    }
    try {
      await api.createHighlight(meetingId, s, en, label.trim() || undefined);
      setStartMs('');
      setEndMs('');
      setLabel('');
      setError(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function onDelete(hid: string): Promise<void> {
    await api.deleteHighlight(meetingId, hid);
    await load();
  }

  return (
    <div className="panel" style={{ marginTop: 12 }}>
      <h3 className="panel__title">Highlights &amp; clips</h3>
      {transcriptLength === 0 && <p className="panel__subtle">Save clips once a transcript is ready.</p>}
      {highlights.length > 0 && (
        <ul className="summary-list" style={{ marginBottom: 10 }}>
          {highlights.map((h) => (
            <li key={h.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <span>
                <span
                  className="summary-topic-time"
                  onClick={() => seek(h.startMs)}
                  role="button"
                >
                  {formatMsClock(h.startMs)}–{formatMsClock(h.endMs)}
                </span>
                {h.label ?? 'Highlight'}
                {h.source === 'auto' && <span className="summary-list__owner">auto</span>}
              </span>
              <button type="button" className="btn btn--ghost" style={{ padding: '2px 8px' }} onClick={() => void onDelete(h.id)}>
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={(e) => void onSave(e)} style={{ display: 'grid', gap: 6 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            placeholder="Start ms"
            value={startMs}
            onChange={(e) => setStartMs(e.target.value)}
            style={{ width: '50%' }}
          />
          <input
            placeholder="End ms"
            value={endMs}
            onChange={(e) => setEndMs(e.target.value)}
            style={{ width: '50%' }}
          />
        </div>
        <input placeholder="Label (optional)" value={label} onChange={(e) => setLabel(e.target.value)} />
        <button type="submit" className="btn btn--ghost">Save highlight</button>
      </form>
      {error && <div className="form-error" style={{ marginTop: 8 }}>{error}</div>}
    </div>
  );
}

export function ActionsPanel({
  meeting,
  onUpdated,
}: {
  meeting: MeetingDetail;
  onUpdated: () => void;
}): JSX.Element {
  const [template, setTemplate] = useState(meeting.templateType ?? 'general');
  const [projectId, setProjectId] = useState('');
  const [createTodos, setCreateTodos] = useState(true);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editProject, setEditProject] = useState(false);

  const canResummarize = meeting.status === 'complete' || meeting.status === 'summary_failed';
  const canBasecamp = meeting.status === 'complete' && meeting.basecampConnected;

  useEffect(() => {
    let alive = true;
    void api.getMeetingPushDefaults(meeting.id).then((defaults) => {
      if (!alive) return;
      const project = defaults.meeting.basecamp.projectId ?? defaults.effective.basecamp.projectId;
      if (project) setProjectId((cur) => cur || project);
    }).catch(() => {});
    return () => {
      alive = false;
    };
  }, [meeting.id]);

  async function onResummarize(): Promise<void> {
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      await api.resummarize(meeting.id, template);
      setMsg(`Re-summarizing with ${templateLabel(template)}…`);
      onUpdated();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function onBasecamp(): Promise<void> {
    if (!projectId.trim()) {
      setErr('Enter a project number, or add one in Integrations.');
      setEditProject(true);
      return;
    }
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      const r = await api.postBasecamp(meeting.id, projectId.trim(), createTodos);
      setMsg(r.todosCreated ? `Posted to Basecamp. Created ${r.todosCreated} to-dos.` : 'Posted to Basecamp.');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (!canResummarize && !canBasecamp) return <></>;

  return (
    <div className="panel" style={{ marginTop: 12 }}>
      <h3 className="panel__title">Summary options</h3>
      {canResummarize && (
        <div style={{ marginBottom: 12 }}>
          <label className="form-label">Re-summarize with template</label>
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            <select value={template} onChange={(e) => setTemplate(e.target.value)}>
              {TEMPLATES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            <button type="button" className="btn btn--ghost" disabled={busy} onClick={() => void onResummarize()}>
              Re-summarize
            </button>
          </div>
        </div>
      )}
      {meeting.status === 'complete' && !meeting.basecampConnected && (
        <p className="panel__subtle">
          To post this summary, <a href="/settings/integrations#basecamp">connect Basecamp</a>.
        </p>
      )}
      {canBasecamp && (
        <div>
          <label className="form-label">Post the summary</label>
          <p className="form-help">Basecamp is one place you can send it. The usual project lives in Integrations.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
            {(!projectId.trim() || editProject) && (
              <input
                placeholder="Project number"
                aria-label="Basecamp project number"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                inputMode="numeric"
              />
            )}
            {projectId.trim() && !editProject && (
              <button type="button" className="btn btn--tertiary btn--sm" style={{ alignSelf: 'flex-start' }} onClick={() => setEditProject(true)}>
                Use a different project
              </button>
            )}
            <label className="toggle">
              <input type="checkbox" checked={createTodos} onChange={(e) => setCreateTodos(e.target.checked)} />
              Also create to-dos from action items. Doing this again will not duplicate them.
            </label>
            <button type="button" className="btn" style={{ alignSelf: 'flex-start' }} disabled={busy} onClick={() => void onBasecamp()}>
              Post summary to Basecamp
            </button>
          </div>
        </div>
      )}
      {msg && <div className="form-success" style={{ marginTop: 8 }}>{msg}</div>}
      {err && <div className="form-error" style={{ marginTop: 8 }}>{err}</div>}
    </div>
  );
}
